import type {
  TwoFactorEnrollBeginResponse,
  TwoFactorEnrollConfirmResponse,
} from '@accounts/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { STR } from '../i18n';
import { ApiError, beginTwoFactorEnrollment, confirmTwoFactorEnrollment } from '../lib/api';
import { brandGlyph, useBranding } from '../lib/branding';
import { queryKeys } from '../lib/queryKeys';
import { CHANGE_PASSWORD_PATH, surfaceById } from '../lib/surfaces';
import { showToast } from '../lib/toast';
import { AppearanceControl } from '../ui/Appearance';
import { SlotBrandMark } from '../ui/BrandMark';
import { Skeleton, Spinner } from '../ui/Primitives';
import { RecoveryCodesPanel } from '../ui/RecoveryCodes';

/**
 * ENROLMENT — the design of record's scan & confirm layout: the QR pane beside the
 * explanation and the code field, then the shown-once recovery codes, then done.
 *
 * One screen serves both ways in: the signed-in volunteer arriving from Security, and the
 * person HELD here at login by the operator's requirement — who holds only the '2fa-pending'
 * cookie, looks signed out to this client, and can reach nothing else until enrollment
 * confirms. The server's `totp-enroll` gate accepts exactly those two credentials and is the
 * enforcement; this page is affordance.
 *
 * SECRET-HANDLING LAW. The TOTP secret and the recovery codes exist in this component's
 * state and nowhere else — never storage, never the query cache, never a URL. The secret is
 * rendered as a real, scannable QR (drawn client-side from the server's `otpauthUri`) plus
 * the base32 key and the otpauth link for manual entry; the codes are gone on dismissal.
 *
 * THE TRANSITION OUT mirrors the login contract exactly: after "Finish set-up",
 * `user.mustChangePassword` routes to the password wall first (carrying the validated
 * destination), then `redirectTo` is honoured, and null lands on the done view. The me query
 * is NOT invalidated before the codes are acknowledged — a route guard reacting to the new
 * credential must never be allowed to yank the shown-once screen away.
 */

/** The demo draws the QR at 150×150 CSS pixels inside the 12px-padded frame. */
const QR_SIZE = 150;

/** Base32 reads best in groups of four — display only; the copy button copies it unbroken. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/gu, '$1 ').trim();
}

type Step = 'scan' | 'codes' | 'done';

/**
 * The begin call's lifecycle, owned by PLAIN COMPONENT STATE — deliberately not
 * `useMutation`. This call fires from the mount effect, and React 18 StrictMode's
 * development-mode effect cycle (setup → cleanup → setup) lands exactly in TanStack's
 * `MutationObserver.onUnsubscribe` window: with its last listener gone the observer
 * detaches from the still-in-flight mutation, nothing re-attaches it on re-subscribe, and
 * the 200 that comes back then updates a mutation nobody is watching — the hook reports
 * `pending` forever and the screen never leaves its skeletons. A resolved promise setting
 * component state has no observer chain to lose, in development or production — and
 * component state is where this response (the plaintext secret) is REQUIRED to live anyway.
 */
type BeginState =
  | { readonly phase: 'pending' }
  | { readonly phase: 'ready'; readonly data: TwoFactorEnrollBeginResponse }
  /** `error` is the server's refusal when it sent one; null for a network-level failure. */
  | { readonly phase: 'failed'; readonly error: ApiError | null };

export function TwoFactorSetupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const branding = useBranding();

  // Passed through to the server untouched and RE-VALIDATED there on confirm — the value
  // in the address bar is the server's own answer from login, but it travelled through the
  // client, so it is untrusted input again the moment it comes back.
  const redirectParam = searchParams.get('redirect');
  const redirect = redirectParam === null || redirectParam === '' ? undefined : redirectParam;

  const [step, setStep] = useState<Step>('scan');
  const [code, setCode] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [rateLimited, setRateLimited] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  /** The pending credential lapsed mid-flow — nothing left to enrol against. */
  const [expired, setExpired] = useState(false);
  /** THE ONE SANCTIONED HOME for the confirm response (recovery codes included). */
  const [confirmed, setConfirmed] = useState<TwoFactorEnrollConfirmResponse | null>(null);

  const codeInputRef = useRef<HTMLInputElement | null>(null);

  const [beginState, setBeginState] = useState<BeginState>({ phase: 'pending' });

  /** Mint (or re-mint) the pending secret. See `BeginState` for why this is not a mutation. */
  const begin = useCallback(() => {
    setBeginState({ phase: 'pending' });
    void (async () => {
      try {
        const data = await beginTwoFactorEnrollment();
        setBeginState({ phase: 'ready', data });
      } catch (error) {
        if (error instanceof ApiError && error.code === 'unauthenticated') {
          setExpired(true);
          return;
        }
        setBeginState({ phase: 'failed', error: error instanceof ApiError ? error : null });
      }
    })();
  }, []);

  // Begin once on mount. The ref guard absorbs StrictMode's double-invoked effects: without
  // it, development would issue two secrets back to back and could render the superseded one.
  const begunRef = useRef(false);
  useEffect(() => {
    if (begunRef.current) return;
    begunRef.current = true;
    begin();
  }, [begin]);

  const confirmMutation = useMutation({
    mutationFn: () =>
      confirmTwoFactorEnrollment(redirect === undefined ? { code } : { code, redirect }),
    onSuccess: (result) => {
      setFormError(null);
      setRateLimited(false);
      // HOLD EVERYTHING; navigate nothing. The codes screen must stand until the person
      // acknowledges it — see the module comment for why the me query stays stale here.
      setConfirmed(result);
      setStep('codes');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        if (error.code === 'rate_limited') {
          setRateLimited(true);
          setFormError(STR.twoFactor.challenge.rateLimited);
          return;
        }
        if (error.code === 'unauthenticated') {
          setExpired(true);
          return;
        }
        setRateLimited(false);
        if (error.code === 'invalid_credentials') {
          setFormError(STR.twoFactor.setup.wrongCode);
          codeInputRef.current?.focus();
          codeInputRef.current?.select();
          return;
        }
        setFormError(error.message);
        return;
      }
      setRateLimited(false);
      setFormError(STR.errors.generic);
    },
  });

  const busy = confirmMutation.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (code.length < 6) {
      setFormError(STR.twoFactor.setup.emptyCode);
      codeInputRef.current?.focus();
      return;
    }
    setFormError(null);
    confirmMutation.mutate();
  }

  /** The gate opens only behind the acknowledgement — same law as the shown-once secret. */
  async function finish() {
    if (!acknowledged || confirmed === null) return;

    // The wall intercepts FIRST, carrying the validated destination — the login contract.
    if (confirmed.user.mustChangePassword) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      const search =
        confirmed.redirectTo === null
          ? ''
          : `?${new URLSearchParams({ redirect: confirmed.redirectTo }).toString()}`;
      navigate(`${CHANGE_PASSWORD_PATH}${search}`, { replace: true });
      return;
    }
    if (confirmed.redirectTo !== null) {
      window.location.assign(confirmed.redirectTo);
      return;
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    void queryClient.invalidateQueries({ queryKey: queryKeys.my2fa });
    setStep('done');
    showToast(STR.toast.twoStepOn);
  }

  const brand = branding.data;
  const beginPending = beginState.phase === 'pending';
  const begun = beginState.phase === 'ready' ? beginState.data : undefined;
  /** `undefined` while not failed; `null` for a failure the server never spoke in. */
  const beginFailure = beginState.phase === 'failed' ? beginState.error : undefined;
  const beginConflict = beginFailure instanceof ApiError && beginFailure.code === 'conflict';

  return (
    <>
      <main>
        <div className="eb-wrap">
          <div className="eb-col">
            <div className="brand-row">
              <SlotBrandMark logoUrl={brand?.logoUrl ?? null} glyph={brand === undefined ? '' : brandGlyph(brand)} />
              {brand === undefined ? (
                <Skeleton style={{ height: 12, width: 120, borderRadius: 5 }} />
              ) : (
                <span className="brand-name">{brand.name}</span>
              )}
            </div>

            {expired ? (
              <div className="card">
                <h1 className="f-title">{STR.twoFactor.setup.title}</h1>
                <div className="stack" style={{ marginTop: 16 }}>
                  <div className="msg call-orange" role="alert">
                    {STR.twoFactor.setup.expired}
                  </div>
                  <div className="actions">
                    <button
                      type="button"
                      className="ghost btn"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() => {
                        navigate(surfaceById('login').path, { replace: true });
                      }}
                    >
                      {STR.twoFactor.setup.backToSignIn}
                    </button>
                  </div>
                </div>
              </div>
            ) : step === 'scan' && beginPending ? (
              // A genuine first paint while the server mints the secret — never a QR that
              // swaps under the camera.
              <div className="card">
                <Skeleton style={{ height: 18, width: 260, borderRadius: 6 }} />
                <Skeleton style={{ height: 11, width: '80%', borderRadius: 5, marginTop: 12 }} />
                <Skeleton style={{ height: QR_SIZE, width: QR_SIZE, borderRadius: 12, marginTop: 22 }} />
              </div>
            ) : step === 'scan' && beginFailure !== undefined ? (
              <div className="card">
                <h1 className="f-title">{STR.twoFactor.setup.title}</h1>
                <div className="stack" style={{ marginTop: 16 }}>
                  <div className={beginConflict ? 'msg call-orange' : 'msg call-red'} role="alert">
                    {beginFailure instanceof ApiError ? beginFailure.message : STR.twoFactor.setup.beginFailed}
                  </div>
                  <div className="actions">
                    {beginConflict ? (
                      // Already enabled: there is nothing to set up. The account surface is
                      // where its state lives — the guard routes an anonymous holder to
                      // sign-in on the way, which is also the honest answer.
                      <button
                        type="button"
                        className="ghost btn"
                        style={{ alignSelf: 'flex-start' }}
                        onClick={() => {
                          navigate(surfaceById('security').path, { replace: true });
                        }}
                      >
                        {STR.twoFactor.setup.viewInAccount}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="ghost btn"
                        style={{ alignSelf: 'flex-start' }}
                        onClick={() => {
                          begin();
                        }}
                      >
                        {STR.twoFactor.setup.tryAgain}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : step === 'scan' && begun !== undefined ? (
              <div className="card card-flush">
                <div className="eb-duo">
                  <div className="eb-scan">
                    <div className="qr-box">
                      {/* A REAL, scannable code — drawn client-side from the server's
                          otpauthUri, at the demo's 150px inside its 12px frame. The fills are
                          the mode-invariant --qr tokens: an inverted QR fails common
                          scanners, so the symbol never follows the dark themes. */}
                      <div
                        className="qr-frame"
                        role="img"
                        aria-label={STR.twoFactor.setup.qrLabel}
                      >
                        <QRCodeSVG
                          value={begun.otpauthUri}
                          size={QR_SIZE}
                          marginSize={3}
                          bgColor="var(--qr-bg)"
                          fgColor="var(--qr-fg)"
                        />
                      </div>
                    </div>
                    <div>
                      <span className="hint" style={{ margin: '0 0 6px', textAlign: 'center' }}>
                        {STR.twoFactor.setup.manualEntry}
                      </span>
                      <div className="secret">
                        <code style={{ fontSize: 11 }}>{groupSecret(begun.secret)}</code>
                        <CopyButton
                          value={begun.secret}
                          label={STR.twoFactor.setup.copyKey}
                          toast={STR.twoFactor.setup.keyCopied}
                        />
                      </div>
                    </div>
                    <div>
                      <span className="hint" style={{ margin: '0 0 6px', textAlign: 'center' }}>
                        {STR.twoFactor.setup.linkEntry}
                      </span>
                      <div className="secret">
                        <code className="wrap" style={{ fontSize: 10.5 }}>{begun.otpauthUri}</code>
                        <CopyButton
                          value={begun.otpauthUri}
                          label={STR.twoFactor.setup.copyLink}
                          toast={STR.twoFactor.setup.linkCopied}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="eb-main">
                    <h1 className="f-title">{STR.twoFactor.setup.title}</h1>
                    <p className="lead">{STR.twoFactor.setup.lead}</p>
                    <div className="stack" style={{ marginTop: 16 }}>
                      {formError === null ? null : (
                        <div className={rateLimited ? 'msg call-orange' : 'msg call-red'} role="alert">
                          {formError}
                        </div>
                      )}
                      <form onSubmit={handleSubmit} noValidate>
                        <div className="stack">
                          <label className="lbl" htmlFor="enroll-code">
                            <span>{STR.twoFactor.setup.codeLabel}</span>
                            <input
                              ref={codeInputRef}
                              className={
                                formError !== null && !rateLimited
                                  ? 'field code-input field-over'
                                  : 'field code-input'
                              }
                              id="enroll-code"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              maxLength={6}
                              placeholder="000000"
                              disabled={busy}
                              value={code}
                              onChange={(event) => {
                                setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                                if (!rateLimited) setFormError(null);
                              }}
                            />
                          </label>
                          <div className="actions">
                            <button
                              className="prim btn btn-main"
                              type="submit"
                              disabled={busy}
                              aria-busy={busy}
                            >
                              {busy ? <Spinner /> : null}
                              <span>
                                {busy ? STR.twoFactor.setup.checking : STR.twoFactor.setup.submit}
                              </span>
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {step === 'codes' && confirmed !== null ? (
              <div className="card">
                <h1 className="f-title">{STR.twoFactor.setup.codesTitle}</h1>
                <p className="lead">{STR.twoFactor.setup.codesLead}</p>
                <div className="stack" style={{ marginTop: 16 }}>
                  <div className="msg call-orange">{STR.twoFactor.setup.codesOnce}</div>
                  <RecoveryCodesPanel
                    codes={confirmed.recoveryCodes}
                    email={confirmed.user.email}
                    acknowledged={acknowledged}
                    onAcknowledgedChange={setAcknowledged}
                    gridStyle={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
                  />
                  <div className="actions">
                    <button
                      type="button"
                      className="prim btn btn-main"
                      disabled={!acknowledged}
                      onClick={() => {
                        void finish();
                      }}
                    >
                      {STR.twoFactor.setup.finish}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {step === 'done' && confirmed !== null ? (
              <div className="card">
                <div className="done-head">
                  <span className="done-dot dot-ok" aria-hidden="true" />
                  <h1 className="f-title">{STR.twoFactor.setup.doneTitle}</h1>
                </div>
                <p className="lead">{STR.twoFactor.setup.doneLead}</p>
                <p className="who">{`${confirmed.user.name} · ${confirmed.user.email}`}</p>
                <div className="actions">
                  <button
                    type="button"
                    className="ghost btn"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={() => {
                      navigate(surfaceById('security').path, { replace: true });
                    }}
                  >
                    {STR.twoFactor.setup.viewInAccount}
                  </button>
                </div>
              </div>
            ) : null}

            <p className="aux">{STR.twoFactor.setup.aux}</p>
          </div>
        </div>
      </main>
      {/* Page furniture, same as the sign-in surfaces: the shared appearance control. */}
      <div className="theme">
        <AppearanceControl />
      </div>
    </>
  );
}

/** The demo's quiet copy affordance beside the manual-entry values. */
function CopyButton({ value, label, toast }: { value: string; label: string; toast: string }) {
  function copy() {
    void (async () => {
      try {
        await navigator.clipboard.writeText(value);
        showToast(toast);
      } catch {
        // The value is on screen and selectable — say the copy failed rather than nothing.
        showToast(STR.twoFactor.codes.copyFailed, 'warn');
      }
    })();
  }

  return (
    <button type="button" className="tbtn copy-btn" onClick={copy}>
      {label}
    </button>
  );
}
